// OnboardingTour.jsx — Interactive onboarding
// Step 0 — Welcome
// Step 1 — Save first prompt (waits for real save)
// Step 2 — Folder drag tutorial (desktop: drag sim in modal / mobile: long-press)
// Step 3 — Chrome extension
// Step 4 — Done

import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { analytics } from '../lib/analytics';

const ACCENT  = '#7f77dd';
const GREEN   = '#1d9e75';
const ORANGE  = '#ef9f27';
const STEPS   = 6;

const PULSE_CSS = `
  @keyframes cpg-pulse {
    0%,100% { box-shadow: 0 0 0 0 rgba(29,158,117,0.7); }
    50%      { box-shadow: 0 0 0 8px rgba(29,158,117,0); }
  }
  @keyframes cpg-bounce {
    0%,100% { transform: translateY(0); }
    50%      { transform: translateY(-5px); }
  }
  @keyframes cpg-drag-hint {
    0%   { transform: translateX(0); opacity: 1; }
    40%  { transform: translateX(28px); opacity: 1; }
    60%  { transform: translateX(28px); opacity: 0.7; }
    100% { transform: translateX(0); opacity: 1; }
  }
  .cpg-pulse  { animation: cpg-pulse  1.5s ease-in-out infinite; }
  .cpg-bounce { animation: cpg-bounce 1.2s ease-in-out infinite; }
  .cpg-drag-hint { animation: cpg-drag-hint 2.2s ease-in-out infinite; }
`;

export default function OnboardingTour({ onClose, onRequestNewEntry, lastSavedEntry, appFormOpen }) {
  const { user, isPro, isAdmin, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [step, setStep]       = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [closing, setClosing] = useState(false);

  // Step 2 state
  const [folderName, setFolderName]       = useState('My Projects');
  const [folderCreated, setFolderCreated] = useState(null);
  const [folderLoading, setFolderLoading] = useState(false);
  const [dragging, setDragging]           = useState(false);
  const [dropped, setDropped]             = useState(false);
  const dropZoneRef = useRef(null);

  // Inject CSS once
  useEffect(() => {
    if (document.getElementById('cpg-ob-css')) return;
    const el = document.createElement('style');
    el.id = 'cpg-ob-css';
    el.textContent = PULSE_CSS;
    document.head.appendChild(el);
    return () => el.remove();
  }, []);

  // Advance from step 1 → 2 only after form is fully closed
  useEffect(() => {
    if (step === 1 && lastSavedEntry && !appFormOpen) {
      setFormOpen(false);
      const t = setTimeout(() => setStep(2), 400);
      return () => clearTimeout(t);
    }
  }, [lastSavedEntry, appFormOpen, step]);

  const handleClose = async (completed = false) => {
    setClosing(true);
    if (user) {
      try { await supabase.from('profiles').update({ has_seen_onboarding: true }).eq('id', user.id); } catch {}
      try { await refreshProfile(); } catch {}
    }
    if (completed) analytics.onboardingCompleted({ stepsSeen: step + 1 });
    else analytics.onboardingSkipped({ atStep: step + 1 });
    setTimeout(() => onClose(completed), 220);
  };

  const handleCreateFolder = async () => {
    if (!folderName.trim()) return;
    setFolderLoading(true);
    await new Promise(r => setTimeout(r, 350));
    setFolderCreated({ id: 'demo', name: folderName.trim() });
    setFolderLoading(false);
  };

  // Drag handlers for prompt card
  const handleDragStart = (e) => {
    setDragging(true);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', 'prompt');
  };
  const handleDragEnd = () => setDragging(false);

  // Drop zone handlers
  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const handleDrop = async (e) => {
    e.preventDefault();
    setDropped(true);
  };

  // ── Shared style helpers ──────────────────────────────────
  const modalBase = {
    position: 'fixed', top: '50%', left: '50%',
    transform: `translate(-50%, -50%) scale(${closing ? 0.96 : 1})`,
    zIndex: 9991, width: '90%', maxWidth: 500,
    background: 'var(--surface)', border: '0.5px solid var(--border2)',
    borderRadius: 20, overflow: 'hidden',
    boxShadow: '0 24px 80px rgba(0,0,0,0.4)',
    opacity: closing ? 0 : 1, transition: 'all 0.22s ease',
    fontFamily: 'var(--font)',
  };

  const bottomBar = {
    ...modalBase, top: 'auto', bottom: 24, left: '50%',
    transform: `translateX(-50%) scale(${closing ? 0.96 : 1})`,
    maxWidth: 460,
  };

  const btnPrimary = (color = ACCENT) => ({
    width: '100%', background: color, color: '#fff', border: 'none',
    borderRadius: 10, padding: '12px 20px',
    fontFamily: 'var(--font)', fontSize: 14, fontWeight: 600,
    cursor: 'pointer', transition: 'filter 0.15s',
    boxShadow: `0 4px 14px ${color}40`,
  });

  const btnSecondary = {
    background: 'none', border: '0.5px solid var(--border2)',
    borderRadius: 10, padding: '11px 18px',
    fontFamily: 'var(--font)', fontSize: 13,
    color: 'var(--muted)', cursor: 'pointer', flexShrink: 0,
  };

  const Backdrop = ({ onClick }) => (
    <div onClick={onClick} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      backdropFilter: 'blur(3px)', zIndex: 9990,
      opacity: closing ? 0 : 1, transition: 'opacity 0.22s',
    }} />
  );

  const ProgressBar = ({ color = ACCENT }) => (
    <div style={{ height: 3, background: 'var(--surface3)' }}>
      <div style={{
        height: '100%',
        width: `${((step + 1) / STEPS) * 100}%`,
        background: color,
        transition: 'width 0.4s ease, background 0.4s ease',
        borderRadius: '0 2px 2px 0',
      }} />
    </div>
  );

  const Dots = ({ color = ACCENT }) => (
    <div style={{ display: 'flex', justifyContent: 'center', gap: 6, margin: '14px 0' }}>
      {Array.from({ length: STEPS }).map((_, i) => (
        <div key={i} style={{
          width: i === step ? 20 : 7, height: 7, borderRadius: 4,
          background: i === step ? color : i < step ? `${color}70` : 'var(--border2)',
          transition: 'all 0.25s',
        }} />
      ))}
    </div>
  );

  const CloseBtn = () => (
    <button onClick={() => handleClose(false)} style={{
      position: 'absolute', top: 14, right: 16,
      background: 'none', border: 'none', fontSize: 18,
      color: 'var(--dim)', cursor: 'pointer', padding: 4,
    }}>×</button>
  );

  const StepLabel = ({ color = ACCENT }) => (
    <div style={{ fontSize: 11, color: 'var(--dim)', marginBottom: 12, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
      Step {step + 1} of {STEPS}
    </div>
  );

  // ── STEP 0: Welcome ───────────────────────────────────────
  if (step === 0) return (
    <>
      <Backdrop onClick={() => handleClose(false)} />
      <div style={modalBase}>
        <ProgressBar />
        <CloseBtn />
        <div style={{ padding: '32px 28px 24px' }}>
          <StepLabel />
          <div style={{ fontSize: 44, marginBottom: 16 }}>✦</div>
          <h2 style={{ fontSize: 22, fontWeight: 600, color: 'var(--text)', letterSpacing: '-0.02em', margin: '0 0 14px' }}>
            Welcome to CopyPasteGenius
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
            {[
              { icon: '📁', title: 'Prompt Library', desc: 'Save your best AI prompts so you never lose them or retype them again.' },
              { icon: '⚡', title: 'Skills', desc: 'Turn a great prompt into a reusable template with fill-in variables.' },
              { icon: '📦', title: 'Playbooks', desc: 'Step-by-step skill bundles for complete workflows — install from the marketplace.' },
              { icon: '🛒', title: 'Marketplace', desc: 'Discover and install Skills & Playbooks built by the community.' },
            ].map(({ icon, title, desc }) => (
              <div key={title} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 10px', background: 'var(--surface)', borderRadius: 8, border: '0.5px solid var(--border)' }}>
                <span style={{ fontSize: 18, flexShrink: 0 }}>{icon}</span>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>{title}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.5 }}>{desc}</div>
                </div>
              </div>
            ))}
          </div>
          <Dots />
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => handleClose(false)} style={btnSecondary}>Skip tour</button>
            <button style={{ ...btnPrimary(), flex: 1 }} onClick={() => setStep(1)}
              onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.1)'}
              onMouseLeave={e => e.currentTarget.style.filter = ''}>
              Let's go →
            </button>
          </div>
        </div>
      </div>
    </>
  );

  // ── STEP 1: Save your first prompt (unique prompts only) ───
  // Modal hides entirely when form is open
  if (step === 1 && (formOpen || appFormOpen)) return null;

  if (step === 1) return (
    <>
      {/* Pulsing ring around + New button */}
      <div style={{
        position: 'fixed', top: 12, right: 16,
        width: 80, height: 36, borderRadius: 8,
        border: `2px solid ${GREEN}`,
        boxShadow: `0 0 0 4px rgba(29,158,117,0.2), 0 0 20px rgba(29,158,117,0.25)`,
        zIndex: 9992, pointerEvents: 'none',
        animation: 'cpg-pulse 1.5s ease-in-out infinite',
      }} />
      <div style={{
        position: 'fixed', top: 52, right: 38,
        zIndex: 9992, pointerEvents: 'none', fontSize: 20,
        animation: 'cpg-bounce 1.2s ease-in-out infinite',
      }}>☝️</div>

      {/* Bottom bar */}
      <div style={{ ...bottomBar, zIndex: 9991 }}>
        <ProgressBar color={GREEN} />
        <CloseBtn />
        <div style={{ padding: '20px 22px 18px' }}>
          <StepLabel color={GREEN} />
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
            💾 Save your first prompt
          </div>
          <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6, margin: '0 0 10px' }}>
            Click the highlighted <strong style={{ color: 'var(--text)' }}>+ New</strong> button above — or tap below and we'll pre-fill a sample so you just need to hit Save.
          </p>
          <div style={{ background: 'rgba(29,158,117,0.07)', border: `0.5px solid rgba(29,158,117,0.2)`, borderRadius: 8, padding: '9px 12px', marginBottom: 12, fontSize: 12, color: 'var(--muted)', lineHeight: 1.6 }}>
            💡 The Chrome extension fills these details automatically from ChatGPT, Claude or Gemini. You can also fill them manually any time.
          </div>
          <Dots color={GREEN} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => handleClose(false)} style={btnSecondary}>Skip</button>
            <button style={{ ...btnPrimary(GREEN), flex: 1 }}
              onClick={() => { onRequestNewEntry?.(); setFormOpen(true); }}
              onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.1)'}
              onMouseLeave={e => e.currentTarget.style.filter = ''}>
              Pre-fill a sample for me →
            </button>
          </div>
        </div>
      </div>
    </>
  );

  // ── STEP 2: Folder drag tutorial ─────────────────────────
  if (step === 2) {
    const isMobile = window.innerWidth < 768;
    const promptTitle = lastSavedEntry?.title || 'My first AI prompt';

    // ── Mobile ───────────────────────────────────────────────
    if (isMobile) return (
      <>
        <Backdrop onClick={() => handleClose(false)} />
        <div style={modalBase}>
          <ProgressBar color={ORANGE} />
          <CloseBtn />
          <div style={{ padding: '28px 24px 22px' }}>
            <StepLabel color={ORANGE} />
            <div style={{ fontSize: 36, marginBottom: 14 }}>📁</div>
            <h2 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)', margin: '0 0 10px' }}>
              Organise your prompts
            </h2>
            <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.65, margin: '0 0 16px' }}>
              On mobile, press and hold any prompt card to move it to a folder or share it with your team.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}>
              {[
                { icon: '👆', title: 'Long press a prompt', desc: 'Hold your finger on any entry card for 1 second' },
                { icon: '📁', title: 'Move to folder', desc: 'Pick "Move to folder" from the action menu' },
                { icon: '👥', title: 'Share to team', desc: 'Pick "Share to team" to add it to your team feed' },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', background: 'var(--surface2)', border: '0.5px solid var(--border)', borderRadius: 10, padding: '11px 13px' }}>
                  <span style={{ fontSize: 18, flexShrink: 0 }}>{item.icon}</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 2 }}>{item.title}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <Dots color={ORANGE} />
            <button style={btnPrimary(ORANGE)} onClick={() => setStep(3)}
              onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.1)'}
              onMouseLeave={e => e.currentTarget.style.filter = ''}>
              Got it, next →
            </button>
          </div>
        </div>
      </>
    );

    // ── Desktop: drag simulation inside modal ────────────────
    return (
      <>
        <Backdrop />
        <div style={{ ...modalBase, maxWidth: 520 }}>
          <ProgressBar color={ORANGE} />
          <CloseBtn />
          <div style={{ padding: '24px 24px 20px' }}>
            <StepLabel color={ORANGE} />
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>
              📁 Organise into a folder
            </div>
            <p style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.6, margin: '0 0 16px' }}>
              {!folderCreated
                ? 'Give your folder a name then create it.'
                : !dropped
                ? `Now drag your prompt into the "${folderCreated.name}" folder below.`
                : `Done! "${promptTitle}" is now in "${folderCreated.name}".`}
            </p>

            {/* ── Sub-step 1: name + create ── */}
            {!folderCreated && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: 'var(--dim)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Step 1 — Name your folder</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    value={folderName}
                    onChange={e => setFolderName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleCreateFolder()}
                    placeholder="My Projects"
                    autoFocus
                    style={{
                      flex: 1, background: 'var(--bg)', color: 'var(--text)',
                      border: `1.5px solid ${ORANGE}`, borderRadius: 8,
                      padding: '9px 12px', fontFamily: 'var(--font)', fontSize: 13, outline: 'none',
                    }}
                  />
                  <button
                    onClick={handleCreateFolder}
                    disabled={folderLoading || !folderName.trim()}
                    style={{ ...btnPrimary(ORANGE), width: 'auto', padding: '9px 16px', fontSize: 13 }}
                    onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.1)'}
                    onMouseLeave={e => e.currentTarget.style.filter = ''}
                  >
                    {folderLoading ? '...' : 'Create →'}
                  </button>
                </div>
              </div>
            )}

            {/* ── Sub-step 2: drag prompt into folder ── */}
            {folderCreated && !dropped && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: 'var(--dim)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Step 2 — Drag your prompt into the folder</div>

                {/* Prompt card (draggable) */}
                <div
                  draggable
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  className={dragging ? '' : 'cpg-drag-hint'}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    background: dragging ? 'var(--surface3)' : 'var(--surface2)',
                    border: `1.5px solid ${dragging ? ORANGE : 'var(--border2)'}`,
                    borderRadius: 9, padding: '10px 14px', marginBottom: 10,
                    cursor: 'grab', userSelect: 'none',
                    opacity: dragging ? 0.5 : 1,
                    transition: 'all 0.15s',
                  }}
                >
                  <span style={{ fontSize: 16 }}>📄</span>
                  <span style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                    {promptTitle}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--dim)', flexShrink: 0 }}>drag me →</span>
                </div>

                {/* Folder drop zone */}
                <div
                  ref={dropZoneRef}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    background: dragging ? `rgba(239,159,39,0.12)` : 'var(--bg)',
                    border: `2px dashed ${dragging ? ORANGE : 'var(--border2)'}`,
                    borderRadius: 9, padding: '12px 14px',
                    transition: 'all 0.15s',
                  }}
                >
                  <span style={{ fontSize: 18 }}>📁</span>
                  <span style={{ fontSize: 13, color: dragging ? ORANGE : 'var(--dim)', fontWeight: dragging ? 600 : 400, transition: 'all 0.15s' }}>
                    {dragging ? `Drop here into "${folderCreated.name}"` : folderCreated.name}
                  </span>
                </div>
              </div>
            )}

            {/* ── Success state ── */}
            {dropped && (
              <div style={{ background: 'rgba(239,159,39,0.08)', border: `0.5px solid rgba(239,159,39,0.3)`, borderRadius: 10, padding: '14px', textAlign: 'center', marginBottom: 16 }}>
                <div style={{ fontSize: 28, marginBottom: 6 }}>🎉</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Prompt moved!</div>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                  "{promptTitle}" is now in "{folderCreated?.name}". In the real app, folders appear in your sidebar.
                </div>
              </div>
            )}

            <Dots color={ORANGE} />

            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setStep(3)} style={btnSecondary}>Skip</button>
              <button
                onClick={() => { if (dropped) setStep(3); }}
                style={{
                  ...btnPrimary(ORANGE), flex: 1,
                  opacity: dropped ? 1 : 0.35,
                  cursor: dropped ? 'pointer' : 'not-allowed',
                }}
                onMouseEnter={e => { if (dropped) e.currentTarget.style.filter = 'brightness(1.1)'; }}
                onMouseLeave={e => e.currentTarget.style.filter = ''}
              >
                {!folderCreated ? 'Create a folder first' : !dropped ? 'Drag the prompt into the folder' : 'Next →'}
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── STEP 3: Chrome extension ──────────────────────────────
  if (step === 3) return (
    <>
      <Backdrop onClick={() => handleClose(false)} />
      <div style={modalBase}>
        <ProgressBar />
        <CloseBtn />
        <div style={{ padding: '28px 28px 22px' }}>
          <StepLabel />
          <div style={{ fontSize: 36, marginBottom: 14 }}>🔌</div>
          <h2 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)', letterSpacing: '-0.02em', margin: '0 0 10px' }}>
            Install the Chrome extension
          </h2>
          <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.7, margin: '0 0 16px' }}>
            Save prompts from ChatGPT, Claude, and Gemini in one click — without leaving the page. Free for all users.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 18 }}>
            {[
              'Smart Save — captures full conversation context automatically',
              'Quick Save — grab just the prompt in one click',
              'Smart Compress — condense long AI chats into a clean summary',
            ].map((t, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, fontSize: 12.5, color: 'var(--muted)' }}>
                <span style={{ color: ACCENT, flexShrink: 0 }}>✓</span>{t}
              </div>
            ))}
          </div>
          <a href="https://chromewebstore.google.com/detail/copypastegenius/hlcfliffepllldfachakilmnkejicgab" target="_blank" rel="noreferrer"
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '11px 14px', borderRadius: 10, textDecoration: 'none', marginBottom: 16,
              background: 'rgba(66,133,244,0.1)', border: '1px solid rgba(66,133,244,0.4)',
              cursor: 'pointer', transition: 'all 0.15s',
            }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
              <circle cx="12" cy="12" r="10" fill="#4285F4"/>
              <circle cx="12" cy="12" r="4.5" fill="white"/>
              <path d="M12 7.5 h9.2" stroke="#EA4335" strokeWidth="3" strokeLinecap="round"/>
              <path d="M12 7.5 C9.5 7.5 7.5 9.5 7.5 12" stroke="#EA4335" strokeWidth="3" strokeLinecap="round" fill="none"/>
              <path d="M12 16.5 L7.6 9" stroke="#34A853" strokeWidth="3" strokeLinecap="round"/>
              <path d="M12 16.5 C13.7 16.5 15.2 15.6 16.1 14.2" stroke="#34A853" strokeWidth="3" strokeLinecap="round" fill="none"/>
              <path d="M16.4 15 L12.3 16.49" stroke="#FBBC05" strokeWidth="3" strokeLinecap="round"/>
              <path d="M21.2 9 L16.4 9" stroke="#EA4335" strokeWidth="3" strokeLinecap="round"/>
            </svg>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>Chrome Extension</div>
              <div style={{ fontSize: 11, color: '#1d9e75' }}>Free · Works with ChatGPT, Claude, Gemini</div>
            </div>
            <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 8, background: 'rgba(29,158,117,0.12)', color: '#1d9e75', border: '0.5px solid rgba(29,158,117,0.3)' }}>Free</span>
          </a>
          <Dots />
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setStep(4)} style={btnSecondary}>Skip</button>
            <button style={{ ...btnPrimary(), flex: 1 }} onClick={() => setStep(4)}
              onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.1)'}
              onMouseLeave={e => e.currentTarget.style.filter = ''}>
              Next →
            </button>
          </div>
        </div>
      </div>
    </>
  );


  // ── STEP 4: Marketplace ───────────────────────────────────
  if (step === 4) return (
    <>
      <Backdrop onClick={() => handleClose(false)} />
      <div style={modalBase}>
        <ProgressBar />
        <CloseBtn />
        <div style={{ padding: '28px 28px 22px' }}>
          <StepLabel />
          <div style={{ fontSize: 36, marginBottom: 14 }}>🛒</div>
          <h2 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)', letterSpacing: '-0.02em', margin: '0 0 10px' }}>
            Discover the Marketplace
          </h2>
          <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.7, margin: '0 0 16px' }}>
            Browse hundreds of skills and playbooks built by the community — install them in one tap and start using them immediately.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}>
            {[
              ['⚡', 'Skills — reusable prompt templates with fill-in variables'],
              ['📦', 'Playbooks — curated bundles of skills for a full workflow'],
              ['🚀', 'Publish your own — share what works and build a following'],
            ].map(([icon, text], i) => (
              <div key={i} style={{ display: 'flex', gap: 10, padding: '9px 12px', background: 'var(--surface2)', borderRadius: 8, border: '0.5px solid var(--border)' }}>
                <span style={{ fontSize: 18, flexShrink: 0 }}>{icon}</span>
                <span style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5 }}>{text}</span>
              </div>
            ))}
          </div>
          <Dots color={GREEN} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setStep(5)} style={btnSecondary}>Skip</button>
            <button
              onClick={() => setStep(5)}
              style={{ ...btnPrimary(GREEN), flex: 1 }}
              onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.1)'}
              onMouseLeave={e => e.currentTarget.style.filter = ''}
            >
              Next →
            </button>
          </div>
        </div>
      </div>
    </>
  );

  // ── STEP 5: Done ─────────────────────────────────────────
  if (step === 5) return (
    <>
      <Backdrop onClick={() => handleClose(true)} />
      <div style={modalBase}>
        <ProgressBar />
        <div style={{ padding: '32px 28px 28px' }}>
          <div style={{ textAlign: 'center', marginBottom: 22 }}>
            <div style={{ fontSize: 52, marginBottom: 10 }}>🎉</div>
            <h2 style={{ fontSize: 21, fontWeight: 600, color: 'var(--text)', letterSpacing: '-0.02em', margin: '0 0 10px' }}>
              You're all set!
            </h2>
            <p style={{ fontSize: 13.5, color: 'var(--muted)', lineHeight: 1.7, maxWidth: 340, margin: '0 auto' }}>
              {isPro || isAdmin
                ? 'Your library is ready. Build your first Skill to turn a great prompt into a reusable template.'
                : 'You\'ve saved your first prompt. Upgrade to unlock unlimited saves, folders, Skills, and the VS Code extension.'}
            </p>
          </div>

          {/* Dashboard — primary, always on top */}
          <button
            onClick={() => { handleClose(true); navigate('/marketplace'); }}
            style={{ ...btnPrimary(), marginBottom: 10 }}
            onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.1)'}
            onMouseLeave={e => e.currentTarget.style.filter = ''}
          >
            Go to Marketplace →
          </button>

          {/* Secondary — plan-aware */}
          {(isPro || isAdmin) ? (
            <Link to="/skills" onClick={() => handleClose(true)}
              style={{ display: 'block', textAlign: 'center', textDecoration: 'none', padding: '10px', borderRadius: 10, border: '0.5px solid var(--border2)', fontSize: 13, color: 'var(--muted)' }}>
              ⚡ Create your first Skill
            </Link>
          ) : (
            <Link to="/premium" onClick={() => handleClose(true)}
              style={{ display: 'block', textAlign: 'center', textDecoration: 'none', fontSize: 13, color: ACCENT, padding: '10px 0' }}>
              Upgrade to Pro — unlock unlimited saves, Skills &amp; more →
            </Link>
          )}
        </div>
      </div>
    </>
  );

  return null;
}
